import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './HealthSpanCard.scss';
import { CATEGORIES } from './behaviorScoreCard.config';
import ScoreSummary from './components/ScoreSummary';
import CategoryScoreGrid from './components/CategoryScoreGrid';
import CategoryScoreList from './components/CategoryScoreList';
import HealthSpanCardLoader from './HealthSpanCardLoader';
import { commonConstant } from '../../../../common';
import { fetchHealthSpanProfileAction } from '../../../../actions/healthSpanActions';
import {
  getCustomerAge,
  getCustomerGender,
} from '../../../../services/healthWellness/healthWellness.service';
import { fetchScoreCategoryMetadata, PROFILE_STATUS } from '../../../../services/healthWellness/healthSpan.service';
import { GENDER } from '../../../../common/commonConstant';
import {
  resolveLevel,
  UI_CATEGORY_TO_METADATA_KEY,
  hasValidScore,
} from '../../../assessment/healthSpanScore.utils';

const CATEGORY_PATHS = {
  nutrition: commonConstant.pathNutritionAssessmentLanding,
  metabolism: commonConstant.pathMetabolismAssessmentLanding,
  exercise: commonConstant.pathExerciseAssessmentLanding,
  stress: commonConstant.pathStressAssessmentLanding,
  gut: commonConstant.pathGutBalanceAssessmentLanding,
  skin: commonConstant.pathSkinAssessmentLanding,
};

const POLL_INTERVAL_MS = 1000;
const POLL_MAX_DURATION_MS = 60000;

const HealthSpanCard = ({ onInfoClick, welcomeModalOpen }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { profile, profileStatus, loading, error } = useSelector((state) => state.healthSpan);
  const bmiData = useSelector((state) => state.bmi);

  const HEALTH_SPAN_VIEW_MODE_KEY = 'healthSpanViewMode';
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem(HEALTH_SPAN_VIEW_MODE_KEY) || 'grid'
  );

  const handleViewModeChange = (mode) => {
    localStorage.setItem(HEALTH_SPAN_VIEW_MODE_KEY, mode);
    setViewMode(mode);
  };
  const [totalMetadata, setTotalMetadata] = useState(null);
  const [categoryMetadata, setCategoryMetadata] = useState({});
  const [ageGroupMetadata, setAgeGroupMetadata] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(true);

  const pollingTimerRef = useRef(null);
  const pollingStartRef = useRef(null);
  const welcomeShownRef = useRef(false);
  const prevWelcomeModalOpenRef = useRef(welcomeModalOpen);

  const customerGender = Object.keys(GENDER).find(
    (item) => GENDER[item] === getCustomerGender()
  );
  const customerAge = getCustomerAge();

  // Initial fetch
  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  // Auto-show welcome modal when status is processing (profile being created)
  useEffect(() => {
    if (profileStatus === PROFILE_STATUS.PROCESSING && !welcomeShownRef.current) {
      welcomeShownRef.current = true;
      if (onInfoClick) {
        onInfoClick();
      }
    }
  }, [profileStatus, onInfoClick]);

  // Detect welcome modal close (transition from open to closed)
  // Then start polling until profile is ready
  useEffect(() => {
    const wasOpen = prevWelcomeModalOpenRef.current;
    prevWelcomeModalOpenRef.current = welcomeModalOpen;

    if (wasOpen && !welcomeModalOpen && welcomeShownRef.current) {
      startPolling();
    }
  }, [welcomeModalOpen]);

  // Start polling when status is processing (from initial fetch, if modal was already shown before)
  useEffect(() => {
    if (profileStatus === PROFILE_STATUS.PROCESSING && !isPolling && welcomeShownRef.current && !welcomeModalOpen) {
      startPolling();
    }
  }, [profileStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const startPolling = useCallback(() => {
    setIsPolling(true);
    pollingStartRef.current = Date.now();

    const poll = () => {
      const elapsed = Date.now() - pollingStartRef.current;
      if (elapsed >= POLL_MAX_DURATION_MS) {
        stopPolling();
        return;
      }

      pollingTimerRef.current = setTimeout(async () => {
        try {
          const result = await dispatch(fetchHealthSpanProfileAction());
          if (result?.status === PROFILE_STATUS.READY) {
            stopPolling();
          } else {
            poll();
          }
        } catch {
          poll();
        }
      }, POLL_INTERVAL_MS);
    };

    poll();
  }, [dispatch]);

  const stopPolling = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsPolling(false);
  };

  // Fetch metadata
  useEffect(() => {
    let cancelled = false;
    setMetadataLoading(true);

    const fetchAllMetadata = async () => {
      try {
        // Fetch total score metadata
        const totalData = await fetchScoreCategoryMetadata('health_span_total_score')
          .then((data) => data?.metadata || null)
          .catch(() => null);
        
        if (!cancelled) setTotalMetadata(totalData);

        // Fetch age group metadata
        const ageData = await fetchScoreCategoryMetadata('health_span_age_group')
          .then((data) => data?.metadata || null)
          .catch(() => null);
        
        if (!cancelled) setAgeGroupMetadata(ageData);

        // Fetch category metadata
        const categoryKeys = Object.keys(UI_CATEGORY_TO_METADATA_KEY);
        const promises = categoryKeys.map((uiKey) => {
          const metadataKey = UI_CATEGORY_TO_METADATA_KEY[uiKey];
          return fetchScoreCategoryMetadata(metadataKey)
            .then((data) => ({ uiKey, metadata: data?.metadata || null }))
            .catch(() => ({ uiKey, metadata: null }));
        });

        const results = await Promise.all(promises);
        if (!cancelled) {
          const metaMap = {};
          results.forEach(({ uiKey, metadata }) => {
            metaMap[uiKey] = metadata;
          });
          setCategoryMetadata(metaMap);
        }
      } finally {
        if (!cancelled) setMetadataLoading(false);
      }
    };

    fetchAllMetadata();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalScore = profile?.totalScore ?? 0;
  const categoryScores = profile?.categoryScores || {};
  const hasTotalScore = hasValidScore(profile?.totalScore);
  const totalLevel = resolveLevel(
    hasTotalScore ? totalScore : null,
    totalMetadata
  );

  const bmi = bmiData?.bmi ?? 0;
  const bmiStatus = getBmiStatus(bmi);

  const age = customerAge || 30;
  const gender = customerGender?.toLowerCase() || 'male';

  const handleCategoryClick = (category) => {
    const path = CATEGORY_PATHS[category.id];
    if (path) {
      history.push(path);
    }
  };

  // Show loader while loading or polling
  if (loading || isPolling || profileStatus === PROFILE_STATUS.PROCESSING || metadataLoading) {
    return <HealthSpanCardLoader />;
  }

  // Show error state when profile not found
  if (profileStatus === PROFILE_STATUS.NOT_FOUND) {
    return (
      <div className='behavior-core-block'>
        <div className="behavior-score-card behavior-score-card--error">
          <div className="behavior-score-card__error">
            <div className="behavior-score-card__error-icon-wrapper">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#FFF2F0"/>
                <path d="M24 14C18.48 14 14 18.48 14 24C14 29.52 18.48 34 24 34C29.52 34 34 29.52 34 24C34 18.48 29.52 14 24 14ZM25 29H23V27H25V29ZM25 25H23V19H25V25Z" fill="#FF524C"/>
              </svg>
            </div>
            <p className="behavior-score-card__error-title">
              ไม่สามารถโหลดข้อมูลได้
            </p>
            <p className="behavior-score-card__error-description">
              กรุณาลองใหม่อีกครั้ง
            </p>
            <button
              className="behavior-score-card__error-btn"
              onClick={() => dispatch(fetchHealthSpanProfileAction())}
            >
              ลองใหม่
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('HealthSpanCard error:', error);
  }

  return (
    <div className='behavior-core-block'>
      <div className="behavior-score-card">
        <ScoreSummary
          totalScore={totalScore}
          scoreColor={totalLevel?.barColor}
          scoreStatus={totalLevel?.statusText}
          bmi={bmi}
          bmiStatus={bmiStatus}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          age={age}
          gender={gender}
          ageGroupMetadata={ageGroupMetadata}
          personImageReady={!bmiData?.hnwBMILoader && !metadataLoading}
          onInfoClick={onInfoClick}
          subtitle=''
        />

        <div className='categories-container'>
          {viewMode === 'grid' && (
            <CategoryScoreGrid
              categories={CATEGORIES}
              categoryScores={categoryScores}
              categoryMetadata={categoryMetadata}
              scoreDetails={profile?.scoreDetails}
              onCategoryClick={handleCategoryClick}
            />
          )}
          {viewMode === 'list' && (
            <CategoryScoreList
              categories={CATEGORIES}
              categoryScores={categoryScores}
              categoryMetadata={categoryMetadata}
              scoreDetails={profile?.scoreDetails}
              onCategoryClick={handleCategoryClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const getBmiStatus = (bmi) => {
  if (!bmi || bmi === 0) return '-';
  if (bmi < 18.5) return 'น้ำหนักต่ำกว่าเกณฑ์';
  if (bmi < 23) return 'ร่างกายสมส่วน';
  if (bmi < 25) return 'อ้วนระดับ 1';
  if (bmi < 30) return 'อ้วนระดับ 2';
  return 'อ้วนระดับ 3';
};

export default HealthSpanCard;
